import { useEffect, useRef, useState } from "react";

import type { MesEvent } from "./types";

export type WsScope =
  | { kind: "global" }
  | { kind: "stage"; stageId: number }
  | { kind: "station"; stationId: string };

export function buildWsUrl(scope: WsScope): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = `${protocol}//${window.location.host}/ws/v1`;
  // Browsers can't set Authorization on a WS handshake, so the backend
  // accepts ?token=<access-token>.  Without it the handshake completes
  // (101) and then the server immediately calls close(1008,
  // 'unauthorized') — which manifests as a flapping online/offline
  // indicator + a Network tab full of 101→reconnect loops.
  const token =
    typeof localStorage === "undefined" ? null : localStorage.getItem("mes:auth:access");
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  switch (scope.kind) {
    case "global":
      return `${base}/global${qs}`;
    case "stage":
      return `${base}/stage/${scope.stageId}${qs}`;
    case "station":
      return `${base}/station/${scope.stationId}${qs}`;
  }
}

/**
 * Subscribe to MES events for the given scope.
 *
 * - Auto-reconnects on connection drop with capped exponential backoff.
 * - Buffers the last `maxBuffer` events in state for components that just
 *   want to render a log.
 */
export function useEventStream(
  scope: WsScope,
  opts: { maxBuffer?: number; enabled?: boolean } = {},
) {
  const { maxBuffer = 50, enabled = true } = opts;
  const [events, setEvents] = useState<MesEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const closedByUs = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    closedByUs.current = false;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(buildWsUrl(scope));
      wsRef.current = ws;
      ws.addEventListener("open", () => {
        if (cancelled) return;
        reconnectAttempt.current = 0;
        setConnected(true);
      });
      ws.addEventListener("message", (e) => {
        if (cancelled) return;
        try {
          const ev = JSON.parse(e.data) as MesEvent;
          setEvents((prev) => {
            const next = [ev, ...prev];
            return next.length > maxBuffer ? next.slice(0, maxBuffer) : next;
          });
        } catch {
          // ignore malformed
        }
      });
      ws.addEventListener("close", () => {
        if (cancelled) return;
        setConnected(false);
        if (closedByUs.current) return;
        const attempt = ++reconnectAttempt.current;
        const delay = Math.min(1000 * 2 ** Math.min(attempt, 5), 15_000);
        setTimeout(() => {
          if (!cancelled) connect();
        }, delay);
      });
      ws.addEventListener("error", () => {
        ws.close();
      });
    };

    connect();
    return () => {
      cancelled = true;
      closedByUs.current = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
    // We intentionally serialize scope into a primitive key to keep the
    // dependency stable across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, "stageId" in scope ? scope.stageId : null, "stationId" in scope ? scope.stationId : null, maxBuffer, enabled]);

  return { events, connected };
}
